/**
 * Presentation rules for the reviewer persona (plan 04 sections 3.2, 3.3 and
 * 3.5; round-3 answers A3 and A4). Pure functions with no React and no path
 * aliases, so Node's test runner covers them (`npm run test:unit`) and the
 * queue, the decision pane, the bell and the AP banner share one wording.
 *
 * Nothing here decides anything about an invoice: which status a decision
 * leads to is Settings `ap.review.decision_targets` on the server. These
 * helpers only turn the server's answer into words.
 */

export type ChipTone = 'success' | 'warning' | 'danger' | 'info' | 'neutral'

type Status = 'open' | 'approved' | 'rejected' | 'info_requested' | 'sent_back' | 'cancelled' | string
/** Plan 10 C6 adds Hold (the request stays open, marked on hold) and Send back. */
type Decision = 'approve' | 'reject' | 'request_info' | 'hold' | 'send_back'
type Filter = 'waiting' | 'decided' | 'all'

export interface Pill {
  label: string
  tone: ChipTone
}

// ─── Queue and toolbar pills ───────────────────────────────────────────────

/**
 * An open request is the invoice status `pending_internal_response`, which the Explorer and
 * Workbench call "Awaiting approval" (`STATUS_LABELS` in lib/ap/invoices.ts). The reviewer
 * pages use the same words; a unit test keeps the two in step.
 */
export const AWAITING_APPROVAL = 'Awaiting approval'

/** Shown on an open request the approver put on hold (plan 10 C6). */
export const ON_HOLD = 'On hold'

/** The queue row pill (plan 04 section 3.2 annotation 4): text, not colour alone. */
export function queueStatusPill(status: Status, held = false): Pill {
  switch (status) {
    case 'open':
      return held ? { label: ON_HOLD, tone: 'info' } : { label: AWAITING_APPROVAL, tone: 'warning' }
    case 'sent_back':
      return { label: 'Sent back', tone: 'warning' }
    case 'approved':
      return { label: 'Approved', tone: 'success' }
    case 'rejected':
      return { label: 'Rejected', tone: 'danger' }
    case 'info_requested':
      return { label: 'Info requested', tone: 'info' }
    case 'cancelled':
      return { label: 'Withdrawn', tone: 'neutral' }
    default:
      return { label: humanise(status), tone: 'neutral' }
  }
}

/** The toolbar state pill in buyer language (plan 04 section 3.3 annotation 1). */
export function buyerStatePill(status: Status, held = false): Pill {
  switch (status) {
    case 'open':
      return held ? { label: 'On hold by you', tone: 'info' } : { label: 'Awaiting your approval', tone: 'warning' }
    case 'sent_back':
      return { label: 'Sent back by you', tone: 'warning' }
    case 'approved':
      return { label: 'Approved by you', tone: 'success' }
    case 'rejected':
      return { label: 'Rejected by you', tone: 'danger' }
    case 'info_requested':
      return { label: 'Info requested', tone: 'info' }
    case 'cancelled':
      return { label: 'Withdrawn by AP', tone: 'neutral' }
    default:
      return { label: humanise(status), tone: 'neutral' }
  }
}

// ─── Oversight wording (admin / AP-Manager viewing someone else's request) ─

/** Who a request is assigned to, as the queue row and the assignment carry it. */
export interface ApproverRef {
  reviewer_name?: string | null
  reviewer_title?: string | null
  reviewer_role?: string | null
}

/** "Jordan Lee (Buyer)": the role is the title's first part, else the humanised role key. */
export function approverLabel(a: ApproverRef | null | undefined): string {
  const name = a?.reviewer_name?.trim() || 'the approver'
  const role = a?.reviewer_title?.split(',')[0]?.trim() || (a?.reviewer_role ? humanise(a.reviewer_role) : '')
  return role ? `${name} (${role})` : name
}

/**
 * The toolbar state pill for an oversight viewer: it names the approver
 * instead of saying "you", because the decision buttons are not theirs.
 */
export function oversightStatePill(status: Status, a: ApproverRef | null | undefined, held = false): Pill {
  const who = approverLabel(a)
  switch (status) {
    case 'open':
      return held ? { label: `On hold with ${who}`, tone: 'info' } : { label: `${AWAITING_APPROVAL} from ${who}`, tone: 'warning' }
    case 'sent_back':
      return { label: `Sent back by ${who}`, tone: 'warning' }
    case 'approved':
      return { label: `Approved by ${who}`, tone: 'success' }
    case 'rejected':
      return { label: `Rejected by ${who}`, tone: 'danger' }
    case 'info_requested':
      return { label: `Info requested by ${who}`, tone: 'info' }
    default:
      return buyerStatePill(status)
  }
}

/** The pill for whoever is looking: "you" wording for the approver, the approver's name otherwise. */
export function statePill(status: Status, oversight: boolean, a: ApproverRef | null | undefined, held = false): Pill {
  return oversight ? oversightStatePill(status, a, held) : buyerStatePill(status, held)
}

/** Eyebrow of the "what is asked" card. */
export function askEyebrow(oversight: boolean): string {
  return oversight ? 'What is asked of the approver' : 'What is asked of you'
}

/** The queue's second column heading: "Why you" for the approver, "Approver" for oversight. */
export function queueReasonHeading(oversight: boolean): string {
  return oversight ? 'Approver' : 'Why you'
}

/** The oversight queue line: who the request waits on, or who it went to once decided. */
export function oversightQueueLine(item: ApproverRef & { status: Status; held_at?: string | null }): string {
  if (item.status !== 'open') return `Sent to ${approverLabel(item)}`
  return item.held_at ? `On hold with ${approverLabel(item)}` : `${AWAITING_APPROVAL} from ${approverLabel(item)}`
}

// ─── Approver sidebar (plan 10 C6, modelled on the IT employee nav) ─────────

export interface ReviewerNavItem {
  key: Filter
  label: string
  href: string
  /** The item shows the waiting count. */
  showsWaiting: boolean
}

/** "My approvals" (waiting, with the count) and "Approved by me" (the approver's decided history). */
export const REVIEWER_NAV: ReviewerNavItem[] = [
  { key: 'waiting', label: 'My approvals', href: '/review', showsWaiting: true },
  { key: 'decided', label: 'Approved by me', href: '/review?filter=decided', showsWaiting: false },
]

/** Which sidebar item is current: the queue or a request opened from it, by its `?filter=`. */
export function reviewerNavActive(pathname: string | null | undefined, filter: string | null | undefined, key: Filter): boolean {
  const path = pathname ?? ''
  if (path !== '/review' && !path.startsWith('/review/')) return false
  return parseQueueFilter(filter) === key && (filter ?? '') !== 'all'
}

/** The queue heading for the approver: the sidebar item's words. */
export function reviewerQueueTitle(filter: Filter): string {
  if (filter === 'decided') return 'Approved by me'
  if (filter === 'all') return 'All my requests'
  return 'My approvals'
}

export interface BriefingFact {
  label: string
  value: string
}

/** The approver briefing at the top of the sidebar: waiting, decided this week, oldest waiting. */
export function approverBriefing(
  summary: { waiting: number; decided_this_week?: number | null; oldest_waiting_at?: string | null; on_hold?: number | null } | null | undefined,
  now: number = Date.now()
): BriefingFact[] {
  if (!summary) return []
  const oldest = summary.waiting > 0 ? relativeAge(summary.oldest_waiting_at, now).replace(/ ago$/, '') : ''
  const facts: BriefingFact[] = [
    { label: 'Waiting', value: String(summary.waiting) },
    { label: 'Decided this week', value: String(summary.decided_this_week ?? 0) },
    { label: 'Oldest waiting', value: oldest || 'None' },
  ]
  if (summary.on_hold) facts.splice(1, 0, { label: 'On hold', value: String(summary.on_hold) })
  return facts
}

export const QUEUE_FILTERS: { key: Filter; label: string }[] = [
  { key: 'waiting', label: AWAITING_APPROVAL },
  { key: 'decided', label: 'Decided' },
  { key: 'all', label: 'All' },
]

/** `?filter=` from the URL; anything unknown falls back to the awaiting-approval filter. */
export function parseQueueFilter(value: string | null | undefined): Filter {
  return value === 'decided' || value === 'all' ? value : 'waiting'
}

/** The queue URL that keeps the filter and search (Back, the pager and the shell link use it). */
export function queueHref(filter: Filter, q?: string | null): string {
  const params = new URLSearchParams()
  if (filter !== 'waiting') params.set('filter', filter)
  if (q && q.trim()) params.set('q', q.trim())
  const text = params.toString()
  return text ? `/review?${text}` : '/review'
}

/** The detail URL; the filter rides along so the pager walks the same list. */
export function detailHref(id: number, filter: Filter): string {
  return filter === 'waiting' ? `/review/${id}` : `/review/${id}?filter=${filter}`
}

/** Client-side search over vendor and invoice number (plan 04 section 3.2 annotation 3). */
export function searchRows<T extends { vendor_name?: string | null; invoice_number?: string | null }>(
  rows: readonly T[],
  q: string | null | undefined
): T[] {
  const needle = (q ?? '').trim().toLowerCase()
  if (!needle) return [...rows]
  return rows.filter(
    (row) =>
      (row.vendor_name ?? '').toLowerCase().includes(needle) ||
      (row.invoice_number ?? '').toLowerCase().includes(needle)
  )
}

// ─── Formatting ────────────────────────────────────────────────────────────

/** "12,480.00 USD" (plan 04 wireframe); a dash when the amount is missing. */
export function formatAmount(value: number | null | undefined, currency?: string | null): string {
  if (value === null || value === undefined || Number.isNaN(value)) return '-'
  const number = value.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
  return currency ? `${number} ${currency}` : number
}

/** "2 h ago", "1 d ago", "just now". `now` is injectable for tests. */
export function relativeAge(iso: string | null | undefined, now: number = Date.now()): string {
  if (!iso) return ''
  const then = new Date(iso).getTime()
  if (Number.isNaN(then)) return ''
  const minutes = Math.max(0, Math.round((now - then) / 60000))
  if (minutes < 1) return 'just now'
  if (minutes < 60) return `${minutes} min ago`
  const hours = Math.round(minutes / 60)
  if (hours < 24) return `${hours} h ago`
  return `${Math.round(hours / 24)} d ago`
}

/** The accessible name of a queue row (plan 04 section 3.2 a11y). */
export function rowAccessibleName(row: {
  invoice_number: string | null
  vendor_name?: string | null
  amount?: number | null
  currency?: string | null
  status: Status
}): string {
  const parts = [row.invoice_number ? `Invoice ${row.invoice_number}` : 'Invoice']
  if (row.vendor_name) parts.push(row.vendor_name)
  if (row.amount !== null && row.amount !== undefined) parts.push(formatAmount(row.amount, row.currency))
  parts.push(queueStatusPill(row.status).label.toLowerCase())
  return parts.join(', ')
}

/** "N / max" under the comment box (ported from Driver `commentCount`). */
export function commentCount(comment: string, maxLength: number): string {
  return `${comment.length} / ${maxLength}`
}

/**
 * The role chip in the reviewer shell: the directory's role label, else the role ("buyer" -> "Buyer"),
 * else the first part of the title ("Buyer, Packaging Materials" -> "Buyer"),
 * else "Approver".
 */
export function roleChipLabel(
  reviewer: { role?: string | null; role_label?: string | null; title?: string | null } | null | undefined
): string {
  // The directory's display label (Settings `ap.review.role_labels`: "ap_manager" -> "AP Manager") wins.
  const label = reviewer?.role_label?.trim()
  if (label) return label
  const role = reviewer?.role?.trim()
  if (role) return humanise(role)
  const title = reviewer?.title?.split(',')[0]?.trim()
  return title || 'Approver'
}

function humanise(value: string): string {
  const text = value.replace(/[_-]+/g, ' ').trim()
  return text ? text.charAt(0).toUpperCase() + text.slice(1) : ''
}

// ─── Decisions ─────────────────────────────────────────────────────────────

export interface DecisionCopy {
  /** Toolbar button label. */
  button: string
  /** Dialog title, with the invoice number filled in. */
  title: (invoiceNumber: string) => string
  /** One consequence sentence (plan 04 decision dialog). */
  consequence: string
  /** Confirm button label. */
  confirm: string
  commentRequired: boolean
  commentLabel: string
  /** The polite live-region text after success, when the server sends none. */
  success: string
}

/**
 * Wording for the three verdicts. A3: a buyer reject returns the invoice to
 * the AP team in Needs Review, so the copy says so (the target itself is
 * Settings on the server).
 */
export const DECISION_COPY: Record<Decision, DecisionCopy> = {
  approve: {
    button: 'Approve',
    title: (n) => `Approve invoice ${n}?`,
    consequence: 'The AP team is told you approved it and continues processing the invoice.',
    confirm: 'Approve invoice',
    commentRequired: false,
    commentLabel: 'Comment (optional)',
    success: 'Approved. The AP team has been notified.',
  },
  reject: {
    button: 'Reject',
    title: (n) => `Reject invoice ${n}?`,
    consequence: 'The invoice goes back to the AP team in Needs Review with your comment.',
    confirm: 'Reject invoice',
    commentRequired: true,
    commentLabel: 'Comment',
    success: 'Rejected. The AP team has been notified.',
  },
  request_info: {
    button: 'Request info',
    title: (n) => `Ask the AP team about invoice ${n}?`,
    consequence: 'The invoice goes back to the AP team with your question.',
    confirm: 'Send question',
    commentRequired: true,
    commentLabel: 'Your question',
    success: 'Question sent. The AP team has been notified.',
  },
  hold: {
    button: 'Hold',
    title: (n) => `Put invoice ${n} on hold?`,
    consequence: 'The invoice stays with you, marked on hold. The AP team sees your reason. You can decide later.',
    confirm: 'Put on hold',
    commentRequired: true,
    commentLabel: 'Reason for the hold',
    success: 'On hold. The invoice stays with you and the AP team can see why.',
  },
  send_back: {
    button: 'Send back',
    title: (n) => `Send invoice ${n} back to the AP team?`,
    consequence: 'The invoice goes back to the AP team in Needs Review so they can fix it and send it again.',
    confirm: 'Send back',
    commentRequired: true,
    commentLabel: 'What should the AP team change?',
    success: 'Sent back. The invoice is back with the AP team in Needs Review.',
  },
}

/** Whether the comment satisfies the verdict's rule. `minLength` comes from the server when it sends one. */
export function commentValid(decision: Decision, comment: string, minLength: number): boolean {
  const text = comment.trim()
  if (!DECISION_COPY[decision].commentRequired) return true
  return text.length >= Math.max(1, minLength)
}

// ─── AP-side status banner (plan 04 section 3.5, A3) ───────────────────────

export interface BannerAssignment {
  status: Status
  /** The server's one-line banner (`AssignmentOut.banner`); preferred over the client wording. */
  banner?: string | null
  reviewer_name?: string | null
  reviewer_title?: string | null
  reviewer_role?: string | null
  requested_at?: string | null
  decided_at?: string | null
  decision_comment?: string | null
  /** Set while the approver holds the open round (plan 10 C6). */
  held_at?: string | null
  hold_reason?: string | null
}

export interface BannerModel {
  tone: ChipTone
  kind: 'approved' | 'rejected' | 'info_requested' | 'sent_back'
  text: string
  /** When the decision was made, already formatted. */
  at: string | null
  /** Offer "Re-send for approval" (info requested, rejected back to Needs Review). */
  canResend: boolean
}

function who(a: BannerAssignment): string {
  return a.reviewer_name?.trim() || 'the approver'
}

function quoted(comment: string | null | undefined): string {
  const text = comment?.trim()
  return text ? `: "${text}"` : ''
}

/**
 * The banner under the Workbench toolbar, AP side. It never names who the
 * invoice is waiting on: an open (or held) round shows no banner, because AP
 * keeps its own actions (Approve sits right next to it) and only the
 * approver's own view (/review) is about the waiting request. Once a round is
 * decided it shows the decision line (approved, rejected, sent back or
 * information requested, with the comment). The server's `banner` line is
 * used when present; the client wording is the fallback. `at` carries the time
 * separately. A cancelled latest round (withdrawn, or closed when AP moved the
 * invoice on) shows no banner. `formatTime` is injected so tests do not depend
 * on the machine's time zone.
 */
export function approvalBanner(
  open: BannerAssignment | null | undefined,
  history: readonly BannerAssignment[] | null | undefined,
  formatTime: (iso: string | null | undefined) => string
): BannerModel | null {
  const server = (a: BannerAssignment) => a.banner?.trim() || null
  if (open && open.status === 'open') return null
  const last = (history ?? []).find((a) => a.status !== 'open')
  if (!last) return null
  const at = last.decided_at ? formatTime(last.decided_at) : null
  const role = (last.reviewer_role ? humanise(last.reviewer_role) : 'approver').toLowerCase()
  switch (last.status) {
    case 'approved':
      return {
        tone: 'success',
        kind: 'approved',
        text: server(last) ?? `Approved by ${who(last)}${quoted(last.decision_comment)}`,
        at,
        canResend: false,
      }
    case 'rejected': {
      // A3: "Rejected by buyer Jordan Lee: <comment>"; the invoice is back in Needs Review.
      const comment = last.decision_comment?.trim()
      return {
        tone: 'danger',
        kind: 'rejected',
        text: server(last) ?? `Rejected by ${role} ${who(last)}${comment ? `: ${comment}` : ''}`,
        at,
        canResend: true,
      }
    }
    case 'sent_back': {
      // Plan 10 C6: back in Needs Review with what to change; AP fixes it and re-sends.
      const comment = last.decision_comment?.trim()
      return {
        tone: 'warning',
        kind: 'sent_back',
        text: server(last) ?? `Sent back by ${role} ${who(last)}${comment ? `: ${comment}` : ''}`,
        at,
        canResend: true,
      }
    }
    case 'info_requested':
      return {
        tone: 'info',
        kind: 'info_requested',
        text: server(last) ?? `${who(last)} asked for more information${quoted(last.decision_comment)}`,
        at,
        canResend: true,
      }
    // A withdrawn round, or one closed by another action (Return to Review, Hold, ...), means the
    // invoice has moved on: no banner on the Workbench; the audit trail keeps the record.
    case 'cancelled':
    default:
      return null
  }
}

/** Audit actions about approvals get the shield glyph in brand purple (plan 04 section 3.5). */
export function isApprovalAuditAction(action: string | null | undefined): boolean {
  return (action ?? '').toLowerCase().includes('approval') || /\bby approver\b/i.test(action ?? '')
}

// ─── Notifications (A4) ────────────────────────────────────────────────────

export type ReviewNotificationKind = 'assigned' | 'decided' | string

export interface ReviewNotification {
  id: string
  assignment_id: number | null
  /** The invoice's database id (AP requester notifications), for the Workbench link. */
  invoice_db_id: number | null
  invoice_number: string | null
  vendor_name: string | null
  kind: ReviewNotificationKind
  title: string
  message: string | null
  created_at: string | null
  unread: boolean
}

export interface ReviewNotificationList {
  items: ReviewNotification[]
  unread: number
}

function asRecord(value: unknown): Record<string, unknown> | null {
  return value && typeof value === 'object' && !Array.isArray(value) ? (value as Record<string, unknown>) : null
}

function str(value: unknown): string | null {
  return typeof value === 'string' && value.trim() ? value : null
}

function num(value: unknown): number | null {
  return typeof value === 'number' && Number.isFinite(value) ? value : null
}

/**
 * Unread count from a notifications payload (`unread_count` on `NotificationsOut`). Accepts `{count}`,
 * `{unread}`, `{unread_count}` or a bare number, so the bell keeps working
 * whichever name the backend settles on; anything else is 0.
 */
export function normaliseUnreadCount(payload: unknown): number {
  if (typeof payload === 'number' && Number.isFinite(payload)) return Math.max(0, Math.floor(payload))
  const record = asRecord(payload)
  if (!record) return 0
  for (const key of ['count', 'unread', 'unread_count']) {
    const value = num(record[key])
    if (value !== null) return Math.max(0, Math.floor(value))
  }
  return 0
}

/** One notification row, tolerant of `seen` / `read` / `unread` / `seen_at`. */
function normaliseNotification(raw: unknown, index: number): ReviewNotification | null {
  const record = asRecord(raw)
  if (!record) return null
  const assignmentId = num(record.assignment_id) ?? num(record.id)
  const invoiceNumber = str(record.invoice_number)
  const vendor = str(record.vendor_name)
  const status = str(record.status)
  const kind = str(record.kind) ?? str(record.type) ?? (status && status !== 'open' ? 'decided' : 'assigned')
  let unread: boolean
  if (typeof record.unread === 'boolean') unread = record.unread
  else if (typeof record.seen === 'boolean') unread = !record.seen
  else if (typeof record.read === 'boolean') unread = !record.read
  else if ('seen_at' in record) unread = record.seen_at === null || record.seen_at === undefined
  else unread = false
  const title =
    str(record.title) ??
    str(record.text) ??
    (kind === 'assigned'
      ? `Approval requested${invoiceNumber ? ` for invoice ${invoiceNumber}` : ''}`
      : `Decision recorded${invoiceNumber ? ` on invoice ${invoiceNumber}` : ''}`)
  return {
    id: String(record.id ?? `${kind}-${assignmentId ?? index}`),
    assignment_id: assignmentId,
    invoice_db_id: num(record.invoice_db_id),
    invoice_number: invoiceNumber,
    vendor_name: vendor,
    kind,
    title,
    message: str(record.message) ?? str(record.headline),
    created_at: str(record.created_at) ?? str(record.at) ?? str(record.notified_at),
    unread,
  }
}

/** `GET /api/review/notifications`: `{items|data|notifications, unread?}` or a bare list. */
export function normaliseNotifications(payload: unknown): ReviewNotificationList {
  const record = asRecord(payload)
  const list = Array.isArray(payload)
    ? payload
    : Array.isArray(record?.items)
      ? (record?.items as unknown[])
      : Array.isArray(record?.data)
        ? (record?.data as unknown[])
        : Array.isArray(record?.notifications)
          ? (record?.notifications as unknown[])
          : []
  const items = list.map(normaliseNotification).filter((n): n is ReviewNotification => n !== null)
  const counted = record ? normaliseUnreadCount(record) : 0
  const unread = counted || items.filter((n) => n.unread).length
  return { items, unread }
}

/** Badge text: nothing at 0, "99+" above 99. */
export function badgeText(count: number): string {
  if (!count || count < 0) return ''
  return count > 99 ? '99+' : String(count)
}

// ─── Cross-component refresh ───────────────────────────────────────────────

/**
 * Window event fired after a decision, a request or an opened notification,
 * so the reviewer shell's counts, the bell and the AP nav badge refresh at
 * once instead of on the next 30 s tick.
 */
export const REVIEW_UPDATED_EVENT = 'ap:review-updated'

export function announceReviewUpdated(): void {
  if (typeof window !== 'undefined') window.dispatchEvent(new CustomEvent(REVIEW_UPDATED_EVENT))
}
